/**
 * 日付入力（@mui/x-date-pickers）のための dayjs の設定（03 1.2 FE-D-004・3.9.1）。
 * 画面の日付入力は JST（Asia/Tokyo）で表示し、送る値は日付のみ `YYYY-MM-DD`、日時は UTC の ISO 文字列（02 2.11）。
 * 比較・今日の判定には使わない（判定は @pcrm/shared/date/jst：要件書 9.2）。
 */
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import 'dayjs/locale/ja';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.locale('ja');

export const JST_TIMEZONE = 'Asia/Tokyo';
export const DATE_DISPLAY_FORMAT = 'YYYY/MM/DD';
export const DATE_TIME_DISPLAY_FORMAT = 'YYYY/MM/DD HH:mm';
/** 入力途中・存在しない日付（2026/02/30 など）の値。Zod の zDate が MSG-V-020 にする */
export const INVALID_DATE_VALUE = 'invalid';

/** `YYYY-MM-DD` → 日付入力の値（空・形の誤りは null） */
export function dateValueOf(value: string | null | undefined): Dayjs | null {
  if (!value || value === INVALID_DATE_VALUE) {
    return null;
  }
  const parsed = dayjs(value, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed : null;
}

/** 日付入力の値 → `YYYY-MM-DD`。空は ''、誤りは 'invalid' */
export function dateStringOf(value: Dayjs | null): string {
  if (value === null) {
    return '';
  }
  return value.isValid() ? value.format('YYYY-MM-DD') : INVALID_DATE_VALUE;
}

/** UTC の ISO 文字列 → 日時入力の値（JST） */
export function dateTimeValueOf(value: string | null | undefined): Dayjs | null {
  if (!value || value === INVALID_DATE_VALUE) {
    return null;
  }
  const parsed = dayjs.utc(value).tz(JST_TIMEZONE);
  return parsed.isValid() ? parsed : null;
}

/** 日時入力の値 → UTC の ISO 文字列（空は ''、誤りは 'invalid'） */
export function dateTimeStringOf(value: Dayjs | null): string {
  if (value === null) {
    return '';
  }
  return value.isValid() ? value.utc().toISOString() : INVALID_DATE_VALUE;
}

/** JST の日付と時刻（例 押した日の 9:00）→ UTC の ISO 文字列 */
export function jstDateTimeToUtc(date: string, time: string): string {
  return dayjs.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', JST_TIMEZONE).utc().toISOString();
}

export { dayjs };
